<template>
  <div class="page-content almacen-page">
    <div class="almacen-wrapper">
      <div class="almacen-card">
        <!-- Header -->
        <header class="historial-header">
          <div>
            <p class="almacen-title">Historial de unidades</p>
            <p class="almacen-subtitle">
              Consulta en qué órdenes ha estado cada unidad, su estado actual y su recorrido entre entrega y recolección.
            </p>
          </div>
        </header>

        <section class="historial-layout">
          <!-- Columna izquierda: productos y unidades -->
          <div class="historial-column historial-column--left">
            <!-- Buscador de productos / series -->
            <div class="historial-search">
              <div class="historial-search-top">
                <div class="historial-mode-toggle">
                  <button
                    type="button"
                    class="mode-pill"
                    :class="{ 'mode-pill--active': modoBusqueda === 'producto' }"
                    @click="cambiarModoBusqueda('producto')"
                  >
                    Por producto
                  </button>
                  <button
                    type="button"
                    class="mode-pill"
                    :class="{ 'mode-pill--active': modoBusqueda === 'serie' }"
                    @click="cambiarModoBusqueda('serie')"
                  >
                    Por número de serie
                  </button>
                </div>
              </div>

              <div class="historial-search-row">
                <input
                  v-model="busquedaProducto"
                  type="text"
                  :placeholder="placeholderBusqueda"
                  @keyup.enter="onEnterBusqueda"
                >
                <button
                  v-if="modoBusqueda === 'serie'"
                  type="button"
                  class="historial-search-btn"
                  :disabled="buscandoSerie"
                  @click="buscarPorNumeroSerie"
                >
                  {{ buscandoSerie ? 'Buscando…' : 'Buscar' }}
                </button>
              </div>
            </div>

            <!-- Lista de productos -->
            <div class="historial-panel">
              <div v-if="cargandoProductos" class="estado-centrado">
                <div class="spinner"></div>
                <p>Cargando productos con unidades…</p>
              </div>

              <div v-else-if="!productosFiltrados.length" class="estado-centrado estado-vacio">
                <p>No se encontraron productos con el filtro actual.</p>
              </div>

              <div v-else class="producto-list">
                <button
                  v-for="p in productosFiltrados"
                  :key="p.id_producto"
                  type="button"
                  class="producto-item"
                  :class="{ 'producto-item--active': productoSeleccionado && productoSeleccionado.id_producto === p.id_producto }"
                  @click="seleccionarProducto(p)"
                >
                  <div class="producto-item-main">
                    <div class="producto-nombre">
                      {{ p.nombre }}
                    </div>
                    <div class="producto-token">
                      {{ p.token_producto }}
                    </div>
                  </div>
                  <div class="producto-metrics">
                    <span class="chip chip-soft">
                      Total: {{ p.total_unidades || 0 }}
                    </span>
                    <span v-if="p.unidades_almacen" class="chip chip-soft chip-soft--almacen">
                      Almacén: {{ p.unidades_almacen }}
                    </span>
                    <span v-if="p.unidades_asignadas" class="chip chip-soft chip-soft--asignado">
                      Asignadas: {{ p.unidades_asignadas }}
                    </span>
                    <span v-if="p.unidades_devuelto" class="chip chip-soft chip-soft--devuelto">
                      Devueltas: {{ p.unidades_devuelto }}
                    </span>
                  </div>
                </button>
              </div>
            </div>

            <!-- Unidades del producto seleccionado -->
            <div class="historial-panel unidades-panel" v-if="productoSeleccionado">
              <div class="panel-header">
                <p class="panel-title">
                  Unidades de: <strong>{{ productoSeleccionado.nombre }}</strong>
                </p>
                <p class="panel-subtitle">
                  Selecciona una unidad para ver su historial detallado.
                </p>
              </div>

              <div v-if="cargandoUnidades" class="estado-centrado">
                <div class="spinner"></div>
                <p>Cargando unidades del producto…</p>
              </div>

              <div v-else-if="!unidadesOrdenadas.length" class="estado-centrado estado-vacio">
                <p>Este producto aún no tiene unidades registradas.</p>
              </div>

              <div v-else class="unidad-list">
                <button
                  v-for="u in unidadesOrdenadas"
                  :key="u.id_unidad"
                  type="button"
                  class="unidad-row"
                  :class="{ 'unidad-row--active': unidadSeleccionada && unidadSeleccionada.id_unidad === u.id_unidad }"
                  @click="seleccionarUnidad(u)"
                >
                  <div class="unidad-main">
                    <div class="unidad-serie">
                      {{ u.numero_serie }}
                    </div>
                    <div class="unidad-token">
                      {{ u.token_unidad }}
                    </div>
                  </div>

                  <div class="unidad-meta">
                    <span class="chip chip-outline" :class="'chip-estado--' + (u.estado || 'almacen')">
                      {{ etiquetaEstadoUnidad(u.estado) }}
                    </span>

                    <span v-if="u.folio_entrada" class="chip chip-soft chip-soft--entrada">
                      Entrada: {{ u.folio_entrada }} · {{ formatFechaCorta(u.fecha_entrada) }}
                    </span>

                    <span v-if="u.ubicacion_actual" class="chip chip-soft chip-soft--ubicacion">
                      {{ u.ubicacion_actual }}
                    </span>
                  </div>
                </button>
              </div>
            </div>
          </div>

          <!-- Columna derecha: historial de la unidad -->
          <div class="historial-column historial-column--right">
            <div class="historial-panel historial-panel--detalle">
              <div v-if="!unidadSeleccionada" class="estado-centrado estado-vacio">
                <p>Selecciona una unidad en la columna izquierda para ver su historial.</p>
              </div>

              <template v-else>
                <!-- Resumen de unidad -->
                <div class="detalle-unidad-header">
                  <div>
                    <p class="detalle-unidad-title">
                      Unidad {{ unidadSeleccionada.numero_serie }}
                    </p>
                    <p class="detalle-unidad-subtitle">
                      Producto: {{ productoSeleccionado ? productoSeleccionado.nombre : '—' }}
                    </p>
                  </div>

                  <div class="detalle-unidad-chips">
                    <span class="chip chip-outline" :class="'chip-estado--' + (unidadSeleccionada.estado || 'almacen')">
                      Estado actual: {{ etiquetaEstadoUnidad(unidadSeleccionada.estado) }}
                    </span>

                    <span
                      v-if="unidadSeleccionada.ubicacion_actual"
                      class="chip chip-soft chip-soft--ubicacion"
                    >
                      Ubicación: {{ unidadSeleccionada.ubicacion_actual }}
                    </span>

                    <span
                      v-if="ultimoMovimiento"
                      class="chip chip-soft chip-soft--ultimo"
                    >
                      Último movimiento:
                      {{ etiquetaEvento(ultimoMovimiento.evento) }}
                      · {{ formatFechaHora(ultimoMovimiento.fecha) }}
                    </span>
                  </div>
                </div>

                <div
                  v-if="unidadSeleccionada.imagen_estado_url || unidadSeleccionada.descripcion_estado"
                  class="detalle-evidencia-actual"
                >
                  <div v-if="unidadSeleccionada.imagen_estado_url" class="detalle-evidencia-actual__photo">
                    <img :src="unidadSeleccionada.imagen_estado_url" alt="Estado actual de la unidad" />
                  </div>

                  <div class="detalle-evidencia-actual__content">
                    <p class="detalle-evidencia-actual__label">Última evidencia física</p>
                    <p class="detalle-evidencia-actual__text">
                      {{ unidadSeleccionada.descripcion_estado || 'Sin descripción registrada.' }}
                    </p>
                  </div>
                </div>

                <!-- Historial -->
                <div v-if="cargandoHistorial" class="estado-centrado">
                  <div class="spinner"></div>
                  <p>Cargando historial de movimientos…</p>
                </div>

                <div v-else-if="!historialOrdenado.length" class="estado-centrado estado-vacio">
                  <p>Aún no hay movimientos registrados para esta unidad.</p>
                </div>

                <div v-else class="timeline">
                  <div
                    v-for="mov in historialOrdenado"
                    :key="mov.id_historial"
                    class="timeline-item"
                  >
                    <div class="timeline-marker">
                      <div
                        class="timeline-dot"
                        :class="'timeline-dot--' + (mov.evento || 'default')"
                      ></div>
                      <div class="timeline-line"></div>
                    </div>

                    <div class="timeline-content">
                      <div class="timeline-header">
                        <span class="timeline-date">
                          {{ formatFechaHora(mov.fecha) }}
                        </span>
                        <span class="chip chip-outline" :class="'chip-evento--' + (mov.evento || 'default')">
                          {{ etiquetaEvento(mov.evento) }}
                        </span>
                      </div>

                      <div class="timeline-body">
                        <p v-if="mov.id_orden_entrada && (!mov.id_orden || (mov.evento || '').toLowerCase() === 'ingreso')" class="timeline-order">
                          Orden de entrada {{ mov.folio_entrada ? ('#' + mov.folio_entrada) : ('#' + mov.id_orden_entrada) }}
                          <span class="timeline-order-status" v-if="mov.fecha_orden_entrada">
                            · Fecha: {{ formatFechaHora(mov.fecha_orden_entrada) }}
                          </span>
                        </p>

                        <p v-if="mov.id_orden" class="timeline-order">
                          Orden {{ mov.folio ? formatoFolioOrden(mov.folio, mov.version) : ('#' + mov.id_orden) }}
                          · {{ etiquetaTipoOrden(mov.tipo_orden) }}
                          <span class="timeline-order-status">
                            · Estado: {{ etiquetaStatusOrden(mov.status_orden) }}
                          </span>
                        </p>

                        <p v-if="mov.centro_nombre" class="timeline-centro">
                          Centro: {{ mov.centro_nombre }}
                        </p>

                        <p v-if="mov.ubicacion_label" class="timeline-centro">
                          Ubicación: {{ mov.ubicacion_label }}
                        </p>

                        <p v-if="mov.observaciones" class="timeline-notes">
                          {{ mov.observaciones }}
                        </p>

                        <div v-if="mov.imagen_estado_url || mov.descripcion_estado" class="timeline-evidence">
                          <div v-if="mov.imagen_estado_url" class="timeline-evidence__photo">
                            <img :src="mov.imagen_estado_url" alt="Evidencia física de la unidad" />
                          </div>
                          <div class="timeline-evidence__body">
                            <p class="detalle-evidencia-actual__label">Evidencia física registrada</p>
                            <p class="timeline-evidence__text">
                              {{ mov.descripcion_estado || 'Sin descripción adicional registrada en este movimiento.' }}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div
                        class="timeline-actions"
                        v-if="mov.id_orden || mov.id_orden_entrada"
                      >
                        <button
                          type="button"
                          class="ios-ghost-button ios-ghost-button--small"
                          @click="irAOrdenDesdeHistorial(mov)"
                        >
                          {{ textoBotonOrden(mov) }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'HistorialUnidades',

  data() {
    return {
      // listados
      productos: [],
      unidades: [],
      historial: [],

      // selección actual
      productoSeleccionado: null,
      unidadSeleccionada: null,

      // estados de carga
      cargandoProductos: false,
      cargandoUnidades: false,
      cargandoHistorial: false,
      buscandoSerie: false,

      // filtros
      busquedaProducto: '',
      modoBusqueda: 'producto', // 'producto' | 'serie'
    };
  },

  computed: {
    placeholderBusqueda() {
      return this.modoBusqueda === 'producto'
        ? 'Buscar producto por nombre o token…'
        : 'Buscar unidad por número de serie o token…';
    },

    productosFiltrados() {
      // En modo serie no filtramos la lista de productos por texto,
      // porque la búsqueda se hace directamente por unidad.
      if (this.modoBusqueda === 'serie') {
        return this.productos;
      }

      const term = this.busquedaProducto.trim().toLowerCase();
      if (!term) return this.productos;

      return this.productos.filter((p) => {
        const nombre = (p.nombre || '').toLowerCase();
        const token = (p.token_producto || '').toLowerCase();
        return nombre.includes(term) || token.includes(term);
      });
    },

    unidadesOrdenadas() {
      return [...this.unidades].sort((a, b) => {
        const sa = (a.numero_serie || '').toString();
        const sb = (b.numero_serie || '').toString();
        return sa.localeCompare(sb, 'es-MX', { numeric: true });
      });
    },

    historialOrdenado() {
      return [...this.historial].sort((a, b) => {
        const da = new Date(a.fecha);
        const db = new Date(b.fecha);
        if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) {
          return (a.id_historial || 0) - (b.id_historial || 0);
        }
        return da - db;
      });
    },

    ultimoMovimiento() {
      if (!this.historialOrdenado.length) return null;
      return this.historialOrdenado[this.historialOrdenado.length - 1];
    },
  },

  mounted() {
    this.cargarProductos();
  },

  methods: {
    /* ----------------- MODO DE BÚSQUEDA ----------------- */

    cambiarModoBusqueda(modo) {
      if (modo === this.modoBusqueda) return;
      this.modoBusqueda = modo;
      this.busquedaProducto = '';
    },

    onEnterBusqueda() {
      if (this.modoBusqueda === 'serie') {
        this.buscarPorNumeroSerie();
      }
    },

    async buscarPorNumeroSerie() {
      const termino = this.busquedaProducto.trim();
      if (!termino) return;

      this.buscandoSerie = true;

      try {
        const res = await axios.get('/api/almacen/historial-unidades/buscar-unidad', {
          params: { numero_serie: termino },
        });
        const payload = res.data || {};
        const unidad = payload?.data?.unidad || null;
        const producto = payload?.data?.producto || null;

        if (!unidad || !producto) {
          this.$toast?.info(
            payload?.message || 'No se encontró ninguna unidad con ese número de serie.'
          );
          return;
        }

        // Buscar el producto en la lista ya cargada para mantener métricas
        const productoLocal = this.productos.find(
          (p) => String(p.id_producto) === String(producto.id_producto)
        );

        this.productoSeleccionado = productoLocal || producto;

        // Cargar unidades de ese producto
        await this.cargarUnidades(this.productoSeleccionado.id_producto);

        // Buscar la unidad en la lista recién cargada
        const unidadLocal = this.unidades.find(
          (u) => String(u.id_unidad) === String(unidad.id_unidad)
        );

        if (unidadLocal) {
          this.seleccionarUnidad(unidadLocal);
        } else {
          // Como respaldo, asignamos la unidad y disparamos carga de historial
          this.unidadSeleccionada = unidad;
          await this.cargarHistorialUnidad(unidad.id_unidad);
        }
      } catch (error) {
        console.error('Error al buscar por número de serie:', error);
        this.$toast?.error('No se pudo buscar la unidad por número de serie.');
      } finally {
        this.buscandoSerie = false;
      }
    },

    /* ----------------- CARGA DE DATOS ----------------- */

    async cargarProductos() {
      this.cargandoProductos = true;
      try {
        const res = await axios.get('/api/almacen/historial-unidades/productos');
        const payload = res.data;

        // El backend responde: { status: 'ok', data: { productos: [...] } }
        this.productos = Array.isArray(payload?.data?.productos)
          ? payload.data.productos
          : [];
      } catch (error) {
        console.error('Error al cargar productos:', error);
        this.$toast?.error('No se pudieron cargar los productos con unidades.');
        this.productos = [];
      } finally {
        this.cargandoProductos = false;
      }
    },

    async cargarUnidades(idProducto) {
      if (!idProducto) return;
      this.cargandoUnidades = true;
      this.unidades = [];
      this.unidadSeleccionada = null;
      this.historial = [];

      try {
        const res = await axios.get('/api/almacen/historial-unidades/unidades', {
          params: { id_producto: idProducto },
        });
        const payload = res.data;

        // Backend: { status: 'ok', data: { unidades: [...] } }
        this.unidades = Array.isArray(payload?.data?.unidades)
          ? payload.data.unidades
          : [];
      } catch (error) {
        console.error('Error al cargar unidades:', error);
        this.$toast?.error('No se pudieron cargar las unidades del producto.');
        this.unidades = [];
      } finally {
        this.cargandoUnidades = false;
      }
    },

    async cargarHistorialUnidad(idUnidad) {
      if (!idUnidad) return;
      this.cargandoHistorial = true;
      this.historial = [];

      try {
        const res = await axios.get('/api/almacen/historial-unidades/detalle', {
          params: { id_unidad: idUnidad },
        });
        const payload = res.data;

        const unidadServidor = payload?.data?.unidad || null;
        const historialServidor = Array.isArray(payload?.data?.historial)
          ? payload.data.historial
          : [];

        if (unidadServidor) {
          this.unidadSeleccionada = {
            ...(this.unidadSeleccionada || {}),
            ...unidadServidor,
          };
        }

        this.historial = historialServidor;
      } catch (error) {
        console.error('Error al cargar historial:', error);
        this.$toast?.error('No se pudo cargar el historial de la unidad.');
        this.historial = [];
      } finally {
        this.cargandoHistorial = false;
      }
    },

    textoBotonOrden(mov) {
      if (!mov) return 'Ver orden';

      const evento = (mov.evento || '').toLowerCase();
      const tipo = (mov.tipo_orden || '').toLowerCase();

      // Órdenes de entrada a almacén
      if (mov.id_orden_entrada && (evento === 'ingreso' || tipo === 'entrada')) {
        return 'Ver orden de entrada';
      }

      // Órdenes de recolección
      if (tipo === 'recoleccion' || evento === 'recoleccion') {
        return 'Ver orden de recolección';
      }

      // Por defecto: orden de entrega
      return 'Ver orden de entrega';
    },

    /* ----------------- ACCIONES UI ----------------- */

    seleccionarProducto(producto) {
      if (
        !producto ||
        (this.productoSeleccionado &&
          this.productoSeleccionado.id_producto === producto.id_producto)
      ) {
        return;
      }
      this.productoSeleccionado = producto;
      this.cargarUnidades(producto.id_producto);
    },

    seleccionarUnidad(unidad) {
      if (
        !unidad ||
        (this.unidadSeleccionada &&
          this.unidadSeleccionada.id_unidad === unidad.id_unidad)
      ) {
        return;
      }
      this.unidadSeleccionada = unidad;
      this.cargarHistorialUnidad(unidad.id_unidad);
    },

    irAOrdenDesdeHistorial(mov) {
      if (!mov) return;

      const evento = (mov.evento || '').toLowerCase();
      const tipo = (mov.tipo_orden || '').toLowerCase();

      let href = '';

      if (mov.id_orden_entrada && (evento === 'ingreso' || tipo === 'entrada')) {
        href = `/dashboard/ver-orden-entrada/${mov.id_orden_entrada}`;
      } else if (mov.id_orden && (tipo === 'recoleccion' || evento === 'recoleccion')) {
        href = `/almacen/ordenes-recoleccion/ver/${mov.id_orden}`;
      } else if (mov.id_orden) {
        href = `/dashboard/ordenes-entrega/${mov.id_orden}`;
      }

      if (!href) return;

      window.open(href, '_blank');
    },

    /* ----------------- FORMATOS / ETIQUETAS ----------------- */

    etiquetaEstadoUnidad(estado) {
      switch (estado) {
        case 'almacen':
          return 'En almacén';
        case 'asignado':
          return 'Asignado';
        case 'resguardo':
          return 'En resguardo';
        case 'merma':
          return 'Merma';
        case 'devuelto':
          return 'Devuelto';
        default:
          return estado || 'Desconocido';
      }
    },

    etiquetaEvento(evento) {
      switch (evento) {
        case 'ingreso':
          return 'Ingreso a almacén';
        case 'salida':
          return 'Salida para entrega';
        case 'resguardo':
          return 'Movimiento a resguardo';
        case 'recoleccion':
          return 'Recolección / devolución';
        case 'merma':
          return 'Movimiento a merma';
        case 'devuelto':
          return 'Devuelto a almacén';
        default:
          return 'Movimiento';
      }
    },

    etiquetaTipoOrden(tipo) {
      switch (tipo) {
        case 'recoleccion':
          return 'Orden de recolección';
        case 'entrega':
        default:
          return 'Orden de entrega';
      }
    },

    etiquetaStatusOrden(status) {
      switch (status) {
        case 'pendiente':
          return 'Pendiente';
        case 'en_proceso':
          return 'En proceso';
        case 'finalizada':
          return 'Finalizada';
        case 'solicitando_ajuste':
          return 'Solicitando ajuste';
        default:
          return status || 'Desconocido';
      }
    },

    formatoFolioOrden(folio, version) {
      if (folio == null || folio === '') return 'SIN FOLIO';
      const n = parseInt(folio, 10);
      const base = Number.isNaN(n)
        ? `ORD-${folio}`
        : `ORD-${String(n).padStart(4, '0')}`;

      if (!version || version <= 1) return base;
      return `${base}-${version}`;
    },

    formatFechaCorta(fechaStr) {
      if (!fechaStr) return 'Sin fecha';
      try {
        const d = new Date(fechaStr);
        if (Number.isNaN(d.getTime())) return fechaStr;
        return d.toLocaleDateString('es-MX', {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
        });
      } catch {
        return fechaStr;
      }
    },

    formatFechaHora(fechaStr) {
      if (!fechaStr) return 'Sin fecha';
      try {
        const d = new Date(fechaStr);
        if (Number.isNaN(d.getTime())) return fechaStr;
        return d.toLocaleString('es-MX', {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        });
      } catch {
        return fechaStr;
      }
    },
  },
};
</script>

<style scoped>
.almacen-page {
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 1.5rem 1rem;
}

.almacen-wrapper {
  width: 100%;
  max-width: 1100px;
}

.almacen-card {
  border-radius: 1.8rem;
  padding: 1.5rem 1.4rem 1.2rem;
  background: radial-gradient(
      circle at 0 0,
      rgba(148, 163, 184, 0.35),
      rgba(15, 23, 42, 0.96)
    );
  backdrop-filter: blur(24px);
  border: 1px solid rgba(148, 163, 184, 0.6);
  box-shadow:
    0 18px 45px rgba(15, 23, 42, 0.9),
    0 0 0 1px rgba(15, 23, 42, 0.85);
}

.almacen-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #e5e7eb;
}

.almacen-subtitle {
  font-size: 0.8rem;
  color: #94a3b8;
}

/* Header */
.historial-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1.1rem;
}

/* Layout principal */
.historial-layout {
  display: grid;
  grid-template-columns: minmax(0, 0.45fr) minmax(0, 0.55fr);
  gap: 0.9rem;
}

/* Columnas */
.historial-column {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
}

/* Paneles internos */
.historial-panel {
  border-radius: 1.2rem;
  border: 1px solid rgba(148, 163, 184, 0.5);
  background: rgba(15, 23, 42, 0.94);
  padding: 0.75rem 0.65rem;
}

.historial-panel--detalle {
  min-height: 260px;
}

/* Buscador */
.historial-search {
  margin-bottom: 0.4rem;
}

.historial-search-top {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.25rem;
}

.historial-mode-toggle {
  display: inline-flex;
  padding: 0.12rem;
  border-radius: 999px;
  background: radial-gradient(
    circle at 0 0,
    rgba(30, 64, 175, 0.9),
    rgba(15, 23, 42, 0.96)
  );
  border: 1px solid rgba(129, 140, 248, 0.9);
}

.mode-pill {
  border: none;
  background: transparent;
  color: #9ca3af;
  font-size: 0.7rem;
  padding: 0.18rem 0.7rem;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.15s ease-out;
}

.mode-pill--active {
  background: radial-gradient(
    circle at 0 0,
    rgba(129, 140, 248, 1),
    rgba(30, 64, 175, 1)
  );
  color: #050816;
  box-shadow: 0 0 14px rgba(129, 140, 248, 0.75);
}

.historial-search-row {
  display: flex;
  gap: 0.35rem;
  align-items: center;
}

.historial-search input {
  width: 100%;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.7);
  background: rgba(15, 23, 42, 0.96);
  padding: 0.45rem 0.9rem;
  font-size: 0.8rem;
  color: #e5e7eb;
  outline: none;
}

.historial-search input::placeholder {
  color: #64748b;
}

.historial-search-btn {
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.8);
  background: rgba(15, 23, 42, 0.98);
  color: #e5e7eb;
  font-size: 0.75rem;
  padding: 0.35rem 0.8rem;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background 0.16s ease,
    border-color 0.16s ease,
    transform 0.08s ease,
    box-shadow 0.16s ease;
}

.historial-search-btn:hover:enabled {
  background: rgba(15, 23, 42, 1);
  transform: translateY(-0.5px);
}

.historial-search-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

/* Productos */
.producto-list {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  max-height: 230px;
  overflow: auto;
}

.producto-item {
  width: 100%;
  text-align: left;
  border-radius: 0.9rem;
  border: 1px solid rgba(148, 163, 184, 0.4);
  background: rgba(15, 23, 42, 0.96);
  padding: 0.4rem 0.55rem;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  transition:
    border-color 0.16s ease,
    background 0.16s ease,
    transform 0.08s ease;
  color: #e5e7eb;
  font-size: 0.78rem;
}

.producto-item--active {
  border-color: rgba(129, 140, 248, 0.95);
  background: radial-gradient(
    circle at 0 0,
    rgba(129, 140, 248, 0.3),
    rgba(15, 23, 42, 0.98)
  );
  transform: translateY(-0.5px);
}

.producto-item-main {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
}

.producto-nombre {
  font-weight: 500;
}

.producto-token {
  font-size: 0.7rem;
  color: #9ca3af;
}

.producto-metrics {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

/* Panel unidades */
.unidades-panel .panel-header {
  margin-bottom: 0.4rem;
}

.panel-title {
  font-size: 0.8rem;
  color: #e5e7eb;
}

.panel-subtitle {
  font-size: 0.72rem;
  color: #9ca3af;
}

.unidad-list {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  max-height: 260px;
  overflow: auto;
}

.unidad-row {
  width: 100%;
  text-align: left;
  border-radius: 0.9rem;
  border: 1px solid rgba(148, 163, 184, 0.4);
  background: rgba(15, 23, 42, 0.98);
  padding: 0.35rem 0.5rem;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.76rem;
  color: #e5e7eb;
  transition:
    border-color 0.16s ease,
    background 0.16s ease,
    transform 0.08s ease;
}

.unidad-row--active {
  border-color: rgba(59, 130, 246, 0.9);
  background: radial-gradient(
    circle at 0 0,
    rgba(59, 130, 246, 0.28),
    rgba(15, 23, 42, 0.98)
  );
  transform: translateY(-0.5px);
}

.unidad-main {
  display: flex;
  justify-content: space-between;
  gap: 0.4rem;
}

.unidad-serie {
  font-weight: 500;
}

.unidad-token {
  font-size: 0.7rem;
  color: #94a3b8;
}

.unidad-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

/* Detalle de unidad (columna derecha) */
.detalle-unidad-header {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  align-items: flex-start;
  margin-bottom: 0.6rem;
}

.detalle-unidad-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #e5e7eb;
}

.detalle-unidad-subtitle {
  font-size: 0.78rem;
  color: #9ca3af;
}

.detalle-unidad-chips {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
}

/* Timeline */
.timeline {
  border-radius: 0.9rem;
  border: 1px solid rgba(148, 163, 184, 0.45);
  background: rgba(15, 23, 42, 0.96);
  padding: 0.55rem 0.7rem;
  max-height: 340px;
  overflow: auto;
}

.timeline-item {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 0.55rem;
  padding: 0.35rem 0;
}

.timeline-marker {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 0.15rem;
}

.timeline-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  border: 2px solid rgba(148, 163, 184, 0.9);
  background: rgba(15, 23, 42, 1);
}

.timeline-dot--ingreso {
  border-color: rgba(34, 197, 94, 0.95);
}

.timeline-dot--salida {
  border-color: rgba(59, 130, 246, 0.95);
}

.timeline-dot--recoleccion {
  border-color: rgba(249, 115, 22, 0.95);
}

.timeline-dot--merma {
  border-color: rgba(248, 113, 113, 0.95);
}

.timeline-line {
  flex: 1;
  width: 2px;
  background: linear-gradient(
    to bottom,
    rgba(148, 163, 184, 0.7),
    rgba(30, 64, 175, 0.3)
  );
  margin-top: 0.15rem;
}

.timeline-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.timeline-header {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  align-items: center;
}

.timeline-date {
  font-size: 0.72rem;
  color: #9ca3af;
}

.timeline-body {
  font-size: 0.78rem;
  color: #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 0.12rem;
}

.timeline-order {
  font-weight: 500;
}

.timeline-order-status {
  font-weight: 400;
  color: #cbd5f5;
}

.timeline-centro {
  color: #cbd5f5;
}

.timeline-notes {
  font-size: 0.74rem;
  color: #cbd5f5;
}

.timeline-actions {
  margin-top: 0.2rem;
}

/* Chips */
.chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  font-size: 0.72rem;
  padding: 0.16rem 0.6rem;
  border: 1px solid transparent;
}

.chip-soft {
  background: rgba(30, 64, 175, 0.4);
  color: #e5e7eb;
  border-color: rgba(148, 163, 184, 0.6);
}

.chip-soft--almacen {
  background: rgba(22, 163, 74, 0.2);
  border-color: rgba(22, 163, 74, 0.9);
}

.chip-soft--asignado {
  background: rgba(59, 130, 246, 0.18);
  border-color: rgba(59, 130, 246, 0.9);
}

.chip-soft--devuelto {
  background: rgba(16, 185, 129, 0.18);
  border-color: rgba(16, 185, 129, 0.9);
}

.chip-soft--entrada {
  background: rgba(55, 65, 81, 0.6);
  border-color: rgba(148, 163, 184, 0.9);
}

.chip-soft--ubicacion {
  background: rgba(79, 70, 229, 0.3);
  border-color: rgba(129, 140, 248, 0.95);
}

.chip-soft--ultimo {
  background: rgba(30, 64, 175, 0.5);
  border-color: rgba(59, 130, 246, 0.95);
}

.chip-outline {
  background: rgba(15, 23, 42, 0.96);
  border-color: rgba(148, 163, 184, 0.8);
  color: #e5e7eb;
}

.chip-estado--almacen {
  border-color: rgba(34, 197, 94, 0.9);
}

.chip-estado--asignado {
  border-color: rgba(59, 130, 246, 0.9);
}

.chip-estado--resguardo {
  border-color: rgba(234, 179, 8, 0.9);
}

.chip-estado--merma {
  border-color: rgba(248, 113, 113, 0.9);
}

.chip-estado--devuelto {
  border-color: rgba(16, 185, 129, 0.9);
}

.detalle-evidencia-actual {
  display: grid;
  grid-template-columns: 140px minmax(0, 1fr);
  gap: 0.9rem;
  margin-bottom: 1rem;
  padding: 0.85rem;
  border-radius: 1rem;
  border: 1px solid rgba(148, 163, 184, 0.32);
  background: rgba(15, 23, 42, 0.5);
}

.detalle-evidencia-actual__photo,
.timeline-evidence__photo {
  overflow: hidden;
  border-radius: 0.9rem;
  border: 1px solid rgba(148, 163, 184, 0.28);
  background: rgba(15, 23, 42, 0.8);
}

.detalle-evidencia-actual__photo img,
.timeline-evidence__photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.detalle-evidencia-actual__photo {
  height: 110px;
}

.detalle-evidencia-actual__label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: #94a3b8;
  margin-bottom: 0.35rem;
}

.detalle-evidencia-actual__text,
.timeline-evidence__text {
  font-size: 0.84rem;
  color: #e5e7eb;
  line-height: 1.45;
}

.timeline-evidence {
  display: grid;
  gap: 0.55rem;
  margin-top: 0.65rem;
}

.timeline-evidence__body {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.timeline-evidence__photo {
  max-width: 220px;
  height: 140px;
}

.chip-evento--ingreso {
  border-color: rgba(34, 197, 94, 0.9);
}

.chip-evento--salida {
  border-color: rgba(59, 130, 246, 0.9);
}

.chip-evento--recoleccion {
  border-color: rgba(249, 115, 22, 0.9);
}

.chip-evento--merma {
  border-color: rgba(248, 113, 113, 0.9);
}

/* Botones iOS */
.ios-ghost-button {
  border-radius: 999px;
  padding: 0.4rem 0.9rem;
  font-size: 0.8rem;
  border: 1px solid rgba(148, 163, 184, 0.7);
  cursor: pointer;
  background: rgba(15, 23, 42, 0.98);
  color: #e5e7eb;
  transition:
    background 0.16s ease,
    border-color 0.16s ease,
    transform 0.08s ease,
    box-shadow 0.16s ease;
}

.ios-ghost-button:hover {
  background: rgba(15, 23, 42, 1);
  transform: translateY(-0.5px);
}

.ios-ghost-button--small {
  padding: 0.25rem 0.7rem;
  font-size: 0.74rem;
}

/* Estados vacíos / carga */
.estado-centrado {
  min-height: 120px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 0.8rem;
  text-align: center;
}

.estado-vacio {
  color: #9ca3af;
}

/* Spinner "spring" */
.spinner {
  width: 26px;
  height: 26px;
  border-radius: 999px;
  border: 3px solid rgba(115, 145, 255, 0.25);
  border-top-color: rgba(132, 165, 255, 0.95);
  animation: spin 0.9s linear infinite;
}
/* Scrollbars finos y discretos para listas de historial / inventario */
.producto-list,
.unidad-list,
.timeline,
.inventario-list,
.unidades-list {
  scrollbar-width: thin;
  scrollbar-color: rgba(148, 163, 184, 0.7) transparent;
}

.producto-list::-webkit-scrollbar,
.unidad-list::-webkit-scrollbar,
.timeline::-webkit-scrollbar,
.inventario-list::-webkit-scrollbar,
.unidades-list::-webkit-scrollbar {
  width: 6px;
}

.producto-list::-webkit-scrollbar-track,
.unidad-list::-webkit-scrollbar-track,
.timeline::-webkit-scrollbar-track,
.inventario-list::-webkit-scrollbar-track,
.unidades-list::-webkit-scrollbar-track {
  background: transparent;
}

.producto-list::-webkit-scrollbar-thumb,
.unidad-list::-webkit-scrollbar-thumb,
.timeline::-webkit-scrollbar-thumb,
.inventario-list::-webkit-scrollbar-thumb,
.unidades-list::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.7);
  border-radius: 999px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive */
@media (max-width: 900px) {
  .historial-layout {
    grid-template-columns: 1fr;
  }

  .detalle-unidad-chips {
    align-items: flex-start;
  }
}

@media (max-width: 768px) {
  .detalle-unidad-header {
    flex-direction: column;
  }
}
</style>
